import { Button, Card } from '@citizen-walk/shared-ui';
import { getBytes, getDownloadURL, ref as storageRef } from 'firebase/storage';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { DistrictMapPmtiles } from '@citizen-walk/shared-types';
import { storage } from '../../lib/firebase';
import { useAnonymousAuth } from '../auth';
import { DistrictPendingScreen, useDistrict, useDistrictState } from '../district';
import {
  deleteOfflineMapEntry,
  fetchPmtilesBlobWithProgress,
  formatBytes,
  getOfflineMapEntry,
  getStorageEstimate,
  putOfflineMapEntry,
  type OfflineMapEntry,
} from './offlineMap';
import {
  buildOfflineGisEntry,
  deleteOfflineGisEntry,
  getOfflineGisEntry,
  gisLayersSignature,
  putOfflineGisEntry,
  type OfflineGisEntry,
} from './offlineGis';
import { useDistrictGisLayers } from './useDistrictGisLayers';
import { useDistrictPmtiles } from './useDistrictPmtiles';

/**
 * オフラインマップ ダウンロードページ (Phase 5.3 R-33 step 4、旧 v1 MapDownloadPage 踏襲)。
 *
 * 機能:
 *   - 地区の最新 mapPmtiles メタデータ表示 (size / maxZoom / 生成日時)
 *   - IndexedDB cache 現状表示 (download 済 / 古い / 未 download)
 *   - DL 進捗 bar (fetchPmtilesBlobWithProgress)
 *   - DL 完了で IndexedDB に永続化 (putOfflineMapEntry)
 *   - 容量見積 (navigator.storage.estimate)
 *   - cache 削除 (deleteOfflineMapEntry)
 *
 * 既知の落とし穴対応:
 *   - 地区フォールバック根絶: 本ページは `/m/:urlToken/map-download` (DistrictProvider 配下) 専用。
 *     地区未解決中は末尾 gate で DistrictPendingScreen を表示し、phase2-default で誤 read しない
 *   - download 中の unmount を考慮し cancelled flag で setState を抑制
 *   - 大容量 (~数百 MB) DL を想定、進捗を loaded/total 両方表示
 *
 * 未実装 (R-33 後続):
 *   - 'auto' policy 連動 (初回 onboarding 時の自動 DL 誘導 dialog)
 *   - Resume (中断時の partial recovery、旧 v1 も Blob 全取得のみ)
 *   - LRU 自動退避 (現状は明示削除のみ)
 */

function formatIsoDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

type DownloadUiState =
  | { status: 'idle' }
  | { status: 'pending'; loaded: number; total: number }
  | { status: 'success' }
  | { status: 'error'; error: Error };

/** GIS は複数レイヤーを逐次 DL するため、進捗はレイヤー件数 (done/total) で表す。 */
type GisDownloadUiState =
  | { status: 'idle' }
  | { status: 'pending'; done: number; total: number }
  | { status: 'success' }
  | { status: 'error'; error: Error };

export function MapDownloadPage() {
  const auth = useAnonymousAuth();
  const district = useDistrict();
  const districtId = district?.id ?? null;
  const districtState = useDistrictState();
  const pmtilesState = useDistrictPmtiles(districtId);
  const [cached, setCached] = useState<OfflineMapEntry | null>(null);
  const [cacheLoaded, setCacheLoaded] = useState(false);
  const [download, setDownload] = useState<DownloadUiState>({ status: 'idle' });
  const [estimate, setEstimate] = useState<{ usageBytes: number; quotaBytes: number } | null>(null);

  // docs/98 R-50: GIS データのオフライン DL。gisEnabled 地区のみ全レイヤーを購読 (overlay 構築は不要)。
  const gis = useDistrictGisLayers(districtId, district?.gisEnabled === true, {
    buildOverlays: false,
  });
  const gisLayers = gis.items.map((i) => i.layer);
  const gisTotalBytes = gisLayers.reduce((sum, l) => sum + l.sizeBytes, 0);
  const [gisCached, setGisCached] = useState<OfflineGisEntry | null>(null);
  const [gisDownload, setGisDownload] = useState<GisDownloadUiState>({ status: 'idle' });
  const gisStale =
    gisCached !== null &&
    gisLayers.length > 0 &&
    gisCached.signature !== gisLayersSignature(gisLayers);

  // cache + storage estimate の初期取得 + DL 完了後 refresh
  const refreshLocalState = useCallback(async () => {
    if (!districtId) {
      setCacheLoaded(true);
      return;
    }
    const [entry, est] = await Promise.all([getOfflineMapEntry(districtId), getStorageEstimate()]);
    setCached(entry ?? null);
    setEstimate(est);
    setCacheLoaded(true);
  }, [districtId]);

  useEffect(() => {
    void refreshLocalState();
  }, [refreshLocalState]);

  const refreshGisState = useCallback(async () => {
    if (!districtId) return;
    const entry = await getOfflineGisEntry(districtId);
    setGisCached(entry ?? null);
  }, [districtId]);

  useEffect(() => {
    void refreshGisState();
  }, [refreshGisState]);

  async function onGisDownload() {
    if (!districtId) return;
    setGisDownload({ status: 'pending', done: 0, total: gisLayers.length });
    try {
      // 各レイヤーの GeoJSON を Storage から取得 (canAccessDistrict read rules)。テキスト化して entry に格納。
      const fetchGeoJson = async (path: string): Promise<string> => {
        const bytes = await getBytes(storageRef(storage, path));
        return new TextDecoder().decode(bytes);
      };
      const entry = await buildOfflineGisEntry(districtId, gisLayers, fetchGeoJson, (done, total) =>
        setGisDownload({ status: 'pending', done, total }),
      );
      await putOfflineGisEntry(districtId, entry);
      setGisDownload({ status: 'success' });
      await refreshGisState();
    } catch (e) {
      setGisDownload({
        status: 'error',
        error: e instanceof Error ? e : new Error(String(e)),
      });
    }
  }

  async function onGisDelete() {
    if (!districtId) return;
    await deleteOfflineGisEntry(districtId);
    await refreshGisState();
    setGisDownload({ status: 'idle' });
  }

  async function onDownload(meta: DistrictMapPmtiles) {
    if (!districtId) return;
    setDownload({ status: 'pending', loaded: 0, total: meta.sizeBytes });
    try {
      const url = await getDownloadURL(storageRef(storage, meta.storagePath));
      const blob = await fetchPmtilesBlobWithProgress(url, (loaded, total) => {
        setDownload({
          status: 'pending',
          loaded,
          total: total || meta.sizeBytes,
        });
      });
      const entry: OfflineMapEntry = {
        storagePath: meta.storagePath,
        schema: meta.schema ?? 'protomaps',
        blob,
        sizeBytes: blob.size,
        maxZoom: meta.maxZoom,
        generatedAt: meta.generatedAt,
        downloadedAt: new Date().toISOString(),
      };
      await putOfflineMapEntry(districtId, entry);
      setDownload({ status: 'success' });
      await refreshLocalState();
    } catch (e) {
      setDownload({
        status: 'error',
        error: e instanceof Error ? e : new Error(String(e)),
      });
    }
  }

  async function onDelete() {
    if (!districtId) return;
    await deleteOfflineMapEntry(districtId);
    await refreshLocalState();
    setDownload({ status: 'idle' });
  }

  const isStale =
    cached &&
    pmtilesState.status === 'ready' &&
    cached.storagePath !== pmtilesState.meta.storagePath;

  // 地区フォールバック根絶: 地区が未解決 (loading/error) の間は phase2-default を使わず、
  // 解決待ちプレースホルダを全画面表示する。以降 district は非 null に narrowing される。
  if (!district) {
    return <DistrictPendingScreen state={districtState} />;
  }

  return (
    <main className="font-sans p-8 max-w-[720px] mx-auto leading-relaxed">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">📥 オフラインマップ</h1>
        <Link
          to=".."
          relative="path"
          className="inline-flex items-center text-xs text-slate-600 hover:text-slate-900 underline min-h-touch"
        >
          ← 戻る
        </Link>
      </div>

      <p className="text-xs text-slate-600 mb-3">
        地区: <strong>{district.name}</strong> (<code>{district.id}</code>)
        {auth.status === 'authenticated' ? null : (
          <span className="ml-2 text-notice-600">※ サインイン待ち</span>
        )}
      </p>

      {/* PMTiles meta */}
      {pmtilesState.status === 'loading' && (
        <Card padding="md">
          <p className="text-sm text-slate-600">読込中…</p>
        </Card>
      )}

      {pmtilesState.status === 'error' && (
        <Card padding="md" className="border-danger-600">
          <p className="text-sm text-danger-700">
            地区情報の取得に失敗しました: {pmtilesState.error.message}
          </p>
          <p className="mt-2 text-xs text-slate-500">
            urlToken 経由でアクセス (`/m/&lt;urlToken&gt;/map-download`)
            されているか確認してください。
          </p>
        </Card>
      )}

      {pmtilesState.status === 'not-found' && (
        <Card padding="md">
          <p className="text-sm text-slate-700">地区情報が見つかりません。</p>
        </Card>
      )}

      {pmtilesState.status === 'absent' && (
        <Card padding="md">
          <p className="text-sm text-slate-700">
            本地区にはまだオフラインマップが用意されていません。管理者にお問い合わせください。
          </p>
        </Card>
      )}

      {pmtilesState.status === 'ready' && (
        <>
          <Card padding="lg" className="mb-4">
            <h2 className="text-base font-bold text-slate-900 mb-2">最新版</h2>
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <dt className="text-slate-500">サイズ</dt>
              <dd className="text-slate-800">{formatBytes(pmtilesState.meta.sizeBytes)}</dd>
              <dt className="text-slate-500">最大 zoom</dt>
              <dd className="text-slate-800">{pmtilesState.meta.maxZoom}</dd>
              <dt className="text-slate-500">生成日時</dt>
              <dd className="text-slate-800">{formatIsoDate(pmtilesState.meta.generatedAt)}</dd>
              <dt className="text-slate-500">schema</dt>
              <dd className="text-slate-800">{pmtilesState.meta.schema ?? 'protomaps'}</dd>
            </dl>
          </Card>

          <Card padding="lg" className="mb-4">
            <h2 className="text-base font-bold text-slate-900 mb-2">本端末のキャッシュ</h2>
            {!cacheLoaded ? (
              <p className="text-sm text-slate-500">確認中…</p>
            ) : cached ? (
              <>
                <p className="text-sm text-slate-700">
                  ✅ 保存済 ({formatBytes(cached.sizeBytes)}、DL:{' '}
                  {formatIsoDate(cached.downloadedAt)})
                </p>
                {isStale && (
                  <p className="mt-2 text-xs text-notice-600">
                    ⚠️ 新しい版が公開されています。「再ダウンロード」で最新版に更新してください。
                  </p>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-500">未保存</p>
            )}
            {estimate && (
              <p className="mt-2 text-[11px] text-slate-400">
                端末ストレージ: {formatBytes(estimate.usageBytes)} /{' '}
                {formatBytes(estimate.quotaBytes)} 使用中
              </p>
            )}
          </Card>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="primary"
              size="md"
              onClick={() => void onDownload(pmtilesState.meta)}
              disabled={download.status === 'pending'}
            >
              {download.status === 'pending'
                ? '保存中…'
                : cached
                  ? isStale
                    ? '再ダウンロード'
                    : '上書きダウンロード'
                  : '保存'}
            </Button>
            {cached && (
              <Button
                variant="secondary"
                size="md"
                onClick={() => void onDelete()}
                disabled={download.status === 'pending'}
              >
                キャッシュ削除
              </Button>
            )}
          </div>

          {download.status === 'pending' && (
            <div className="mt-3">
              <p className="text-xs text-slate-600">
                {formatBytes(download.loaded)} / {formatBytes(download.total)}
                {download.total > 0
                  ? ` (${((download.loaded / download.total) * 100).toFixed(0)}%)`
                  : ''}
              </p>
              <div
                className="mt-1 h-2 bg-slate-200 rounded overflow-hidden"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={download.total || 100}
                aria-valuenow={download.loaded}
              >
                <div
                  className="h-full bg-primary-600 transition-all"
                  style={{
                    width:
                      download.total > 0 ? `${(download.loaded / download.total) * 100}%` : '0%',
                  }}
                />
              </div>
            </div>
          )}

          {download.status === 'success' && (
            <p className="mt-3 text-xs text-success-600">✅ 保存完了</p>
          )}

          {download.status === 'error' && (
            <p className="mt-3 text-xs text-danger-700 break-words">
              ❌ 失敗: <code>{download.error.message}</code>
            </p>
          )}
        </>
      )}

      {/* docs/98 R-50: GIS データ (重ね地図) のオフライン DL。gisEnabled 地区のみ表示。
          PMTiles (ベース地図) とは別に、行政界 / 施設 / ハザード等の重畳レイヤーを端末へ保存する。 */}
      {district.gisEnabled === true && (
        <Card padding="lg" className="mb-4 mt-2">
          <h2 className="text-base font-bold text-slate-900 mb-2">🗺 GIS データ (重ね地図)</h2>
          {gis.loading && gisLayers.length === 0 ? (
            <p className="text-sm text-slate-500">読込中…</p>
          ) : gisLayers.length === 0 ? (
            <p className="text-sm text-slate-700">
              この地区には公開中の GIS レイヤーがありません。
            </p>
          ) : (
            <>
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs mb-2">
                <dt className="text-slate-500">レイヤー数</dt>
                <dd className="text-slate-800">{gisLayers.length}</dd>
                <dt className="text-slate-500">合計サイズ (概算)</dt>
                <dd className="text-slate-800">{formatBytes(gisTotalBytes)}</dd>
              </dl>

              {gisCached ? (
                <p className="text-sm text-slate-700">
                  ✅ 保存済 ({gisCached.layers.length} レイヤー /{' '}
                  {formatBytes(gisCached.totalBytes)}、DL: {formatIsoDate(gisCached.downloadedAt)})
                </p>
              ) : (
                <p className="text-sm text-slate-500">未保存</p>
              )}
              {gisStale && (
                <p className="mt-2 text-xs text-notice-600">
                  ⚠️ レイヤーが更新されています。「再ダウンロード」で最新版に更新してください。
                </p>
              )}

              <div className="mt-3 flex flex-wrap items-center gap-3">
                <Button
                  variant="primary"
                  size="md"
                  onClick={() => void onGisDownload()}
                  disabled={gisDownload.status === 'pending'}
                >
                  {gisDownload.status === 'pending'
                    ? '保存中…'
                    : gisCached
                      ? gisStale
                        ? '再ダウンロード'
                        : '上書きダウンロード'
                      : '保存'}
                </Button>
                {gisCached && (
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={() => void onGisDelete()}
                    disabled={gisDownload.status === 'pending'}
                  >
                    キャッシュ削除
                  </Button>
                )}
              </div>

              {gisDownload.status === 'pending' && (
                <div className="mt-3">
                  <p className="text-xs text-slate-600">
                    {gisDownload.done} / {gisDownload.total} レイヤー
                    {gisDownload.total > 0
                      ? ` (${((gisDownload.done / gisDownload.total) * 100).toFixed(0)}%)`
                      : ''}
                  </p>
                  <div
                    className="mt-1 h-2 bg-slate-200 rounded overflow-hidden"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={gisDownload.total || 1}
                    aria-valuenow={gisDownload.done}
                  >
                    <div
                      className="h-full bg-primary-600 transition-all"
                      style={{
                        width:
                          gisDownload.total > 0
                            ? `${(gisDownload.done / gisDownload.total) * 100}%`
                            : '0%',
                      }}
                    />
                  </div>
                </div>
              )}
              {gisDownload.status === 'success' && (
                <p className="mt-3 text-xs text-success-600">✅ 保存完了</p>
              )}
              {gisDownload.status === 'error' && (
                <p className="mt-3 text-xs text-danger-700 break-words">
                  ❌ 失敗: <code>{gisDownload.error.message}</code>
                </p>
              )}
            </>
          )}
        </Card>
      )}

      <p className="mt-6 text-[11px] text-slate-400">
        ※ Phase 5.3 R-33 step 4 — オフライン時は MapPage が本キャッシュを優先使用します。 DL
        完了後に MapPage を開くと PMTiles 表示に切替わります (R-33 step 7)。GIS データは保存後、
        圏外でも地図に重畳表示されます (docs/98 R-50)。
      </p>
    </main>
  );
}
